import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IChapter } from '../../components/widgets/types';
import { useDossier } from '../DossierContext';

/**
 * Gestion CRUD des chapitres d'un dossier.
 *
 * Consomme le `DossierContext` (pas de props). Les setters viennent
 * du provider, le rendu n'a plus à les drill.
 */
export function useChapterManagement() {
  const { dossier, updateDossier, currentChapterIndex, setCurrentChapterIndex } = useDossier();

  const addChapter = useCallback(() => {
    let newIndex = 0;
    updateDossier(draft => {
      draft.content.push({
        _id: uuidv4(),
        type: 'chapter',
        title: 'Nouveau chapitre',
        sections: [],
      });
      newIndex = draft.content.length - 1;
    });
    setCurrentChapterIndex(newIndex);
  }, [updateDossier, setCurrentChapterIndex]);

  const editChapter = useCallback(
    (index: number, newTitle: string) => {
      updateDossier(draft => {
        if (!draft.content?.[index]) {
          console.error('editChapter: index hors limites', index);
          return;
        }
        draft.content[index].title = newTitle;
      });
    },
    [updateDossier]
  );

  const reorderChapters = useCallback(
    (updatedChapters: IChapter[]) => {
      updateDossier(draft => {
        draft.content = updatedChapters;
      });
    },
    [updateDossier]
  );

  const deleteChapter = useCallback(
    (index: number) => {
      let newLength = 0;
      updateDossier(draft => {
        if (!draft.content?.[index]) {
          console.error('deleteChapter: index hors limites', index);
          return;
        }
        draft.content.splice(index, 1);
        newLength = draft.content.length;
      });

      const nextIndex =
        currentChapterIndex >= newLength
          ? Math.max(0, newLength - 1)
          : currentChapterIndex === index
          ? Math.max(0, currentChapterIndex - 1)
          : currentChapterIndex;

      setCurrentChapterIndex(nextIndex);
    },
    [updateDossier, currentChapterIndex, setCurrentChapterIndex]
  );

  return {
    dossier,
    currentChapterIndex,
    addChapter,
    editChapter,
    reorderChapters,
    deleteChapter,
  };
}

export default useChapterManagement;
